import {
  ToolCallAccordion,
  ToolCallAccordionContent,
  ToolCallAccordionItem,
  ToolCallAccordionTrigger,
  type ToolCallState,
} from "@arcadeai/design-system/components/ui/molecules/tool-call-accordion";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import type { AuthInfo } from "../types";
import { ToolAuthStatusCard } from "./tool-invocation-auth/tool-auth-status-card";
import {
  getToolkitName,
  resolveToolAuthCard,
} from "./tool-invocation-auth/utils";
import { getToolInvocationErrorSummary } from "./tool-invocation-error";

type ToolInvocationProps = {
  name: string;
  state: ToolCallState;
  args?: Record<string, unknown>;
  result?: unknown;
  authInfoMap?: Map<string, AuthInfo>;
  onRetryAuth?: () => void;
};

export function ToolInvocation({
  name,
  state,
  args = {},
  result,
  authInfoMap,
  onRetryAuth,
}: ToolInvocationProps) {
  const authCard = resolveToolAuthCard({ authInfoMap, name, result });

  if (authCard) {
    return (
      <ToolAuthStatusCard
        args={args}
        authInfo={authCard.authInfo}
        displayName={authCard.displayName}
        message={authCard.message}
        name={name}
        onRetryAuth={onRetryAuth}
        status={authCard.status}
        url={authCard.url}
      />
    );
  }

  const errorSummary =
    getToolInvocationErrorSummary(result) ??
    (state === "error"
      ? {
          title: "Tool execution failed",
          description: "The tool returned an error before completing.",
        }
      : null);
  const resolvedState: ToolCallState = errorSummary ? "error" : state;
  const hasResult =
    (resolvedState === "complete" || resolvedState === "error") &&
    result !== undefined;
  const hasArgs = Object.keys(args).length > 0;
  const hasContent = hasArgs || hasResult || Boolean(errorSummary);
  const ToolkitIcon = getToolkitIcon(getToolkitName(name));

  return (
    <ToolCallAccordion className="my-2 w-full" collapsible type="single">
      <ToolCallAccordionItem value="item">
        <ToolCallAccordionTrigger
          icon={<ToolkitIcon className="size-3.5" />}
          state={resolvedState}
          toolName={name}
        />
        {hasContent && (
          <ToolCallAccordionContent
            args={hasArgs ? args : undefined}
            className="max-h-[min(32rem,70vh)]"
            errorNotice={errorSummary ?? undefined}
            result={hasResult ? result : undefined}
          />
        )}
      </ToolCallAccordionItem>
    </ToolCallAccordion>
  );
}
